<template>
  <header class="editor-toolbar" aria-label="大屏编辑工具栏">
    <div class="editor-toolbar__project">
      <el-tooltip :content="contain.isTemplateEditing ? (contain.isComponentResourceEditing ? '返回组件资源中心' : '返回模板资源中心') : '返回大屏列表'" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button editor-project-home" :aria-label="contain.isTemplateEditing ? (contain.isComponentResourceEditing ? '返回组件资源中心' : '返回模板资源中心') : contain.projectRecord && contain.projectRecord.type === 'scada2d' ? '返回2D组态列表' : '返回大屏列表'" @click="handleHome"><i aria-hidden="true" class="el-icon-monitor"></i></button>
      </el-tooltip>
      <pages ref="pages" v-if="!contain.isTemplateEditing || (contain.templateRecord && contain.templateRecord.kind === 'project')" @open-change="onPageMenuChange"></pages>
      <div v-else class="editor-project-label"><span class="editor-project-name">{{ contain.isComponentResourceEditing ? '组件' : contain.isComponentTemplateEditing ? '组件模板' : '页面模板' }} · {{ contain.templateRecord ? contain.templateRecord.name : '正在加载' }}</span><span id="editor-save-status" class="editor-save-status" :class="'is-' + contain.editorSaveStatus" role="status" aria-live="polite" :title="contain.editorSaveStatusDescription">{{ contain.editorSaveStatusText }}</span></div>
    </div>
    <div class="editor-toolbar__tools">
      <project-version-history v-if="!contain.isTemplateEditing && canQueryVersions" ref="history" :save-draft="handleBuild" @open-change="onHistoryChange" />
      <span v-if="!contain.isTemplateEditing && canQueryVersions" class="editor-toolbar__divider" aria-hidden="true"></span>
      <el-tooltip content="撤销（Ctrl + Z）" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button" aria-label="撤销编辑" :disabled="!contain.undoStack.length" @click="contain.undoStructure"><i aria-hidden="true" class="el-icon-refresh-left"></i></button>
      </el-tooltip>
      <el-tooltip content="恢复（Ctrl + Shift + Z）" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button" aria-label="恢复编辑" :disabled="!contain.redoStack.length" @click="contain.redoStructure"><i aria-hidden="true" class="el-icon-refresh-right"></i></button>
      </el-tooltip>
      <span class="editor-toolbar__divider"></span>
      <global-filter v-if="!contain.isComponentTemplateEditing" ref="filter" @open-change="onFilterChange" />
      <el-tooltip :content="contain.editorTheme === 'dark' ? '切换到浅色模式' : '切换到深色模式'" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button" aria-label="切换编辑器明暗模式" @click="contain.editorTheme = contain.editorTheme === 'dark' ? 'light' : 'dark'"><i aria-hidden="true" :class="contain.editorTheme === 'dark' ? 'el-icon-sunny' : 'el-icon-moon'"></i></button>
      </el-tooltip>
      <el-tooltip v-if="!contain.isComponentTemplateEditing" content="快捷操作" placement="bottom" :disabled="pageMenuOpen || filterOpen">
        <button ref="quick" type="button" class="editor-icon-button" aria-label="打开快捷操作" aria-haspopup="dialog" aria-controls="editor-quick-dialog" :disabled="!contain.editorSaveReady || contain.editorVersionSwitching" @click="openQuickOperation"><i aria-hidden="true" class="el-icon-lightning"></i></button>
      </el-tooltip>
      <el-tooltip content="显示或收起组件栏" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button" aria-label="切换组件栏" :aria-expanded="String(contain.paletteVisible)" @click="contain.paletteVisible = !contain.paletteVisible"><i aria-hidden="true" class="el-icon-menu"></i></button>
      </el-tooltip>
      <el-tooltip content="显示或收起属性栏" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button" aria-label="切换属性栏" :aria-expanded="String(contain.inspectorVisible)" @click="contain.inspectorVisible = !contain.inspectorVisible"><i aria-hidden="true" class="el-icon-setting"></i></button>
      </el-tooltip>
    </div>
    <div class="editor-toolbar__actions">
      <quick-operation v-if="quickVisible" :visible="quickVisible" @update:visible="closeQuickOperation" />
      <el-tooltip content="在新窗口预览" placement="bottom" :disabled="pageMenuOpen">
        <button type="button" class="editor-icon-button" aria-label="预览大屏" :disabled="previewing || !contain.editorSaveReady || contain.editorVersionSwitching" @click="handleView"><i aria-hidden="true" :class="previewing ? 'el-icon-loading' : 'iconfont icon-view'"></i></button>
      </el-tooltip>
      <el-button v-if="contain.isTemplateEditing" size="small" class="editor-save" :loading="saving" :disabled="!contain.editorSaveReady || contain.editorVersionSwitching || !!contain.templateSaveBlocked" aria-describedby="editor-save-status" aria-keyshortcuts="Control+S Meta+S" title="保存模板（Ctrl + S）" @click="handleSaveAndReturn"><i aria-hidden="true" class="iconfont icon-build"></i>{{ contain.isComponentResourceEditing ? '保存组件' : '保存模板' }}</el-button>
      <el-button v-if="!contain.isTemplateEditing && canPublish" type="primary" size="small" :disabled="!contain.editorSaveReady || saving || contain.editorVersionSwitching" @click="openPublication">发布</el-button>
      <project-version-dialog v-if="versionsVisible && contain.projectRecord" :visible.sync="versionsVisible" :theme="contain.editorTheme" :project-id="contain.id" :name="contain.projectRecord.name" :dirty="contain.editorDirty || saving" :expected-metadata="contain.projectRecord.metadata" :disabled="contain.projectRecord.state === 'disabled' || (contain.projectRecord.state && contain.projectRecord.state.value === 'disabled')" @status="contain.projectVersionStatus = $event" @changed="contain.projectVersionStatus = $event" @locate="locatePublicationIssue" />
      <el-dropdown trigger="click" placement="bottom-end" @command="handleZoom" class="editor-zoom-dropdown">
        <button type="button" class="editor-zoom-button" aria-label="画布缩放">{{ Math.round(contain.editorViewport.zoom) }}% <i aria-hidden="true" class="el-icon-arrow-down"></i></button>
        <el-dropdown-menu slot="dropdown" class="editor-zoom-menu">
          <el-dropdown-item command="in">放大 <span class="editor-shortcut">+</span></el-dropdown-item>
          <el-dropdown-item command="out">缩小 <span class="editor-shortcut">−</span></el-dropdown-item>
          <el-dropdown-item command="30" divided>缩放至 30%</el-dropdown-item>
          <el-dropdown-item command="50">缩放至 50%</el-dropdown-item>
          <el-dropdown-item command="100">缩放至 100%</el-dropdown-item>
          <el-dropdown-item command="200">缩放至 200%</el-dropdown-item>
          <el-dropdown-item command="400">缩放至 400%</el-dropdown-item>
          <el-dropdown-item command="fit" divided>缩放到合适尺寸</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </header>
</template>

<script>
import { uuid } from '@/utils/utils'
import baseList from '@/option/base'
import { projectVersionStatus, revisionPayload } from '@/utils/project-versions'
import backgroundDrafts from '@/runtime/background-drafts'
import ProjectVersionDialog from '@/components/ProjectVersions/dialog.vue'
import { cloneConfig, createLegacySavePayload } from '@/schema/legacy-adapter'
import { createPreviewSnapshot } from '@/runtime/preview-snapshot'
import { createPageSavePayload, snapshotPages } from '@/schema/page-project'
import ProjectVersionHistory from '@/components/ProjectVersions/history.vue'
import pages from './pages.vue'
import store from '@/store'
import { updateTemplate } from '@/api/templates'
import { updateTemplate as updateComponentResource } from '@/api/component-resources'
import { savedComponentResource } from '@/utils/component-resources'
import { saveResourceCover } from '@/utils/resource-cover'
import { savedTemplate } from '@/utils/templates'
import { templateEditorPayload, readTemplatePreview } from '@/editor/core/template-editor'
import { locateNode } from '@/editor/core/node-operations'
import { limitNodeBounds } from '@/editor/core/component-size'
import GlobalFilter from './global-filter.vue'
import QuickOperation from './quick-operation.vue'
export default {
  inject: ["contain"],
  components: { pages, ProjectVersionDialog, ProjectVersionHistory, GlobalFilter, QuickOperation },
  data () {
    return {
      baseList: baseList,
      pageMenuOpen: false,
      filterOpen: false,
      quickVisible: false,
      versionsVisible: false,
      previewing: false
    }
  },
  computed: {
    saving () { return this.contain.editorSaveInProgress },
    canQueryVersions () { return store.getters['auth/hasResourcePermission']('visualization:query') },
    canPublish () { return this.canQueryVersions && store.getters['auth/hasResourcePermission']('visualization:publish') }
  },
  beforeDestroy () { if (this._previewTask && !this._previewTask.tab.closed) this._previewTask.tab.close(); },
  methods: {
    onPageMenuChange (open) { this.pageMenuOpen = open; if (open) { if (this.$refs.history) this.$refs.history.close(false); if (this.$refs.filter) this.$refs.filter.close(false) } },
    onHistoryChange (open) { if (open) { if (this.$refs.pages) this.$refs.pages.close(false); if (this.$refs.filter) this.$refs.filter.close(false) } },
    onFilterChange (open) { this.filterOpen = open; if (open) { if (this.$refs.pages) this.$refs.pages.close(false); if (this.$refs.history) this.$refs.history.close(false) } },
    closeToolbarPanels () { if (this.$refs.pages) this.$refs.pages.close(false); if (this.$refs.history) this.$refs.history.close(false); if (this.$refs.filter) this.$refs.filter.close(false) },
    openQuickOperation () { if (!this.contain.editorSaveReady || this.contain.editorVersionSwitching) return; this.closeToolbarPanels(); this.quickVisible = true },
    closeQuickOperation () { this.quickVisible = false; this.$nextTick(() => { if (!this._isDestroyed && this.$refs.quick) this.$refs.quick.focus() }) },
    openPublication () { this.closeToolbarPanels(); this.versionsVisible = true },
    locatePublicationIssue (issue) {
      if (!this.contain.editorSaveReady || this.contain.editorVersionSwitching) return
      if (issue.pageId && issue.pageId !== this.contain.activePageId) this.contain.switchPage(issue.pageId)
      const found = issue.componentId && locateNode(this.contain.nav, issue.componentId)
      let node = found && found.node
      if (!node) { let nodes = this.contain.nav; for (const index of issue.componentPath || []) { node = nodes && nodes[index]; nodes = node && node.children } }
      this.contain.inspectorVisible = true
      if (node) this.contain.active = [node.index]
      this.$nextTick(() => { this.contain.tabsActive = node ? issue.tab : '0' })
    },
    handleZoom (command) {
      if (this.$refs.filter) this.$refs.filter.close(false)
      const container = this.contain.$refs.container;
      if (command === 'fit') container.setScale();
      else container.zoomTo(command === 'in' ? this.contain.editorViewport.zoom + 10 : command === 'out' ? this.contain.editorViewport.zoom - 10 : Number(command));
    },
    handleMenuMouseDown (event) {
      if (!event.target.closest('[data-preserve-selection]')) this.contain.handleMouseDown();
    },
    //判断大屏是否为模板例子
    vaildData(id){ //'big-screen',
      return ['big-screen1', 'big-screen2', 'big-screen3', 'big-screen4'].includes(id)
    },
    handleView () {
      if (this._previewTask) return this._previewTask.promise;
      if (this.contain.editorVersionSwitching) return Promise.resolve(false);
      if (!this.contain.projectRecord || !this.contain.editorSaveReady) {
        this.$message.warning('请等待大屏配置加载完成后再预览');
        return Promise.resolve(false);
      }
      // 点击时打开独立预览窗口，模板保存也可复用这个预留页。
      const tab = window.open('about:blank', '_blank');
      if (!tab) {
        this.$message.warning('预览窗口被浏览器拦截，请允许此站点弹出窗口后重试');
        return Promise.resolve(false);
      }
      tab.opener = null;
      const task = { tab, projectId: this.contain.id, generation: this.contain.editorSaveGeneration,
        pageId: this.contain.activePageId, pageSwitchRequest: this.contain.editorPageSwitchRequest };
      this._previewTask = task;
      this.previewing = true;
      task.promise = this.saveAndOpenPreview(task).finally(() => {
        if (this._previewTask === task) { this._previewTask = null; this.previewing = false; }
      });
      return task.promise;
    },
    async saveAndOpenPreview (task) {
      const tab = task.tab;
      const contain = this.contain;
      const current = () => !this._isDestroyed && !tab.closed && task.projectId === contain.id &&
        task.generation === contain.editorSaveGeneration && task.pageId === contain.activePageId &&
        task.pageSwitchRequest === contain.editorPageSwitchRequest && contain.editorSaveReady &&
        !contain.editorVersionSwitching && contain.projectRecord && contain.projectRecord.id === task.projectId;
      try {
        do {
          if (!current()) { if (!tab.closed) tab.close(); return false; }
          const saved = await contain.saveEditorBeforeAction(task.generation);
          await contain.$nextTick();
          if (!saved || !current() || contain.editorSaveInProgress || contain.editorSaveError) {
            if (!tab.closed) tab.close();
            return false;
          }
          // 屏障返回前后的新编辑也必须补存；不能用旧草稿版本套上新快照。
        } while (contain.editorDirty);
        let controlVersionId;
        if (!contain.isTemplateEditing) {
          const status = projectVersionStatus(contain.projectVersionStatus);
          if (!/^[A-Za-z0-9_-]{1,64}$/.test(status.draftVersionId) ||
              !contain.editorContentSignature || contain.editorContentSignature !== contain.editorSavedSignature) {
            throw new Error('已保存草稿版本与当前内容不一致，请重新保存后预览');
          }
          controlVersionId = status.draftVersionId;
        }
        // 组件模板预览使用已保存的内容边界，不读取编辑工作区或缩放尺寸。
        const componentModel = this.contain.isComponentTemplateEditing ? readTemplatePreview(this.contain.templateRecord, this.contain.config) : null;
        const config = componentModel ? componentModel.config : this.contain.config;
        const nodes = componentModel ? componentModel.nodes : this.contain.nav;
        const project = componentModel ? componentModel.pageProject : this.contain.pageProject;
        const pageId = componentModel ? componentModel.activePageId : this.contain.activePageId;
        const record = componentModel ? componentModel.record : this.contain.projectRecord;
        const metadata = { ...(componentModel ? componentModel.metadata : this.contain.obj) };
        delete metadata.previewControlVersionId;
        if (this.contain.isTemplateEditing) metadata.templatePreview = true;
        else metadata.previewControlVersionId = controlVersionId;
        const query = {};
        if (project) {
          metadata.pageProject = snapshotPages(project, pageId, config, nodes);
          query.page = pageId;
        }
        // 版本锚点只进入这份临时快照，不写回项目元数据，也不触发发布。
        const preview = createPreviewSnapshot(record, metadata, config, nodes, tab.sessionStorage);
        const route = this.$router.resolve({ name: 'view', params: { id: this.contain.id }, query: { ...query, preview } });
        tab.location.replace(route.href);
        return true;
      } catch (error) {
        if (!tab.closed) tab.close();
        this.$message.error(error.message || '无法创建预览，请稍后重试');
        return false;
      }
    },
    handleReset () {
      this.contain.menuFlag = true;
      this.contain.setScale(this.contain.contentWidth);
    },
    async handleSaveAndReturn () {
      const contain = this.contain;
      if (this._isDestroyed || !contain.isTemplateEditing || !contain.editorSaveReady || contain.editorVersionSwitching || contain.templateSaveBlocked) return false;
      const id = contain.id, generation = contain.editorSaveGeneration;
      const current = this._saveAndReturnTask;
      if (current && current.id === id && current.generation === generation) return false;
      const task = { id, generation };
      this._saveAndReturnTask = task;
      try {
        const saved = await this.handleBuild();
        await contain.$nextTick();
        // 只有按钮保存会返回；预览、Ctrl+S和离开前保存继续复用原入口。
        if (!saved || this._isDestroyed || id !== contain.id || generation !== contain.editorSaveGeneration ||
          !contain.editorSaveReady || contain.editorSaveInProgress || contain.editorVersionSwitching || contain.editorDirty || contain.templateSaveBlocked) return false;
        return await this.handleHome();
      } finally { if (this._saveAndReturnTask === task) this._saveAndReturnTask = null; }
    },
    handleBuild (options = {}) {
      if (this.contain.editorVersionSwitching) return Promise.resolve(false);
      if (!this.contain.isTemplateEditing) return this.persistDraft(options);
      const current = this._saveTask;
      if (current && current.projectId === this.contain.id && current.generation === this.contain.editorSaveGeneration) {
        if (options.background && current.promoteBackground) current.promoteBackground();
        return current.promise;
      }
      if (this.contain.editorSaveInProgress) return Promise.resolve(false);
      const task = { projectId: this.contain.id, generation: this.contain.editorSaveGeneration, background: Boolean(options.background) };
      task.backgroundSignal = new Promise(resolve => { task.promoteBackground = () => { task.background = true; resolve(null); }; });
      this._saveTask = task;
      task.promise = this.persistDraft({ ...options, task }).finally(() => { if (this._saveTask === task) this._saveTask = null; });
      return task.promise;
    },
    async persistDraft (options = {}) {
      if (this.contain.isTemplateEditing) return this.saveTemplateContent(options);
      if (!this.contain.projectRecord || this.contain.projectRecord.id !== this.contain.id) {
        this.$message.warning('大屏配置尚未加载成功，不能保存');
        return;
      }
      let payload;
      let ticket;
      const projectId = this.contain.id;
      let versionName;
      try {
        // 一次捕获完整快照；请求在独立队列中串行执行，不等待截图或阻止切页。
        payload = this.contain.pageProject
          ? createPageSavePayload(this.contain.projectRecord, this.contain.obj, this.contain.pageProject, this.contain.activePageId, this.contain.config, this.contain.nav)
          : createLegacySavePayload(this.contain.projectRecord, this.contain.obj, this.contain.config, this.contain.nav);
        revisionPayload(this.contain.projectVersionStatus);
        if (typeof options.versionName === 'string') {
          const name = options.versionName.trim();
          if (!name || name.length > 128 || Array.from(name).some(char => char.charCodeAt(0) < 32 || (char.charCodeAt(0) >= 127 && char.charCodeAt(0) <= 159))) throw new Error('请输入1至128个字符的版本名称，不能包含控制字符');
          versionName = name;
        }
        ticket = this.contain.editorSaveTicket || this.contain.beginEditorSave();
        const pending = backgroundDrafts.enqueue({ record: cloneConfig(this.contain.projectRecord), status: cloneConfig(this.contain.projectVersionStatus), metadata: payload.metadata,
          signature: this.contain.editorContentSignature, baselineSignature: this.contain.editorSavedSignature, pageId: options.pageId || this.contain.activePageId, versionName });
        if (this.contain._editorDraftObserver) this.contain._editorDraftObserver();
        const contain = this.contain;
        contain._editorDraftObserver = backgroundDrafts.observe(projectId, entry => {
          if (!contain.isCurrentEditorSave(ticket)) return;
          if (entry.result) {
            contain.obj = JSON.parse(entry.record.metadata);
            contain.visual = cloneConfig(contain.obj.visual);
            contain.projectRecord = entry.record;
            contain.projectVersionStatus = entry.status;
            contain.editorSavedSignature = entry.savedSignature;
          }
          if (entry.error) {
            if (contain.failEditorSave(new Error(entry.error), ticket) && !this._isDestroyed) this.$message.error(entry.error);
          }
          else if (!entry.active && !entry.jobs.length) contain.completeEditorSave(ticket, entry.savedSignature);
        });
        const saved = await pending;
        if (saved && !options.quiet && !this._isDestroyed && ticket.generation === contain.editorSaveGeneration) this.$message.success(contain.editorDirty ? '本次配置已保存，仍有新的修改未保存' : '大屏配置保存成功');
        return saved;
      } catch (error) {
        this.contain.failEditorSave(error, ticket);
        if (!this._isDestroyed) this.$message.error(error.message);
        return;
      }
    },
    async saveTemplateContent (options = {}) {
      const resource = this.contain.isComponentResourceEditing;
      const permission = resource ? 'visualizationComponent' : 'visualizationTemplate';
      let ticket;
      let submitted = false;
      try {
        if (!store.getters['auth/hasResourcePermission'](permission + ':query') || !store.getters['auth/hasResourcePermission'](permission + ':save')) throw new Error('当前账号没有模板保存权限');
        const previous = this.contain.templateRecord;
        if (!previous || previous.id !== this.contain.id || !previous.editable) throw new Error('模板尚未加载成功或不可编辑');
        if (this.contain.isComponentTemplateEditing && this.contain.templateSaveBlocked) throw new Error(this.contain.templateSaveBlocked);
        if (this.contain.isComponentTemplateEditing && this.contain.editorDirty === false) return { record: this.contain.projectRecord, signature: this.contain.editorSavedSignature };
        const payload = templateEditorPayload(previous, this.contain.config, this.contain.nav, this.contain.pageProject, this.contain.activePageId);
        const record = this.contain.projectRecord && (this.contain.pageProject
          ? { ...this.contain.projectRecord, ...createPageSavePayload(this.contain.projectRecord, this.contain.obj, this.contain.pageProject, this.contain.activePageId, this.contain.config, this.contain.nav) }
          : { ...this.contain.projectRecord, ...createLegacySavePayload(this.contain.projectRecord, this.contain.obj, this.contain.config, this.contain.nav) });
        if (payload.visibility === 'public' && !store.getters['auth/hasResourcePermission'](permission + ':publish')) throw new Error(previous.isSystem ? '保存系统组件需要组件公开权限，请联系管理员授权' : '保存公开资源需要对应的公开权限，请授权或在编辑信息中改为私有');
        ticket = this.contain.beginEditorSave();
        payload.coverFileId = await saveResourceCover(JSON.parse(payload.snapshot), () => !this._isDestroyed && this.contain.isCurrentEditorSave(ticket));
        if (this._isDestroyed || !this.contain.isCurrentEditorSave(ticket)) return;
        if (!store.getters['auth/hasResourcePermission'](permission + ':save') || (payload.visibility === 'public' && !store.getters['auth/hasResourcePermission'](permission + ':publish'))) throw new Error('资源权限已变更，本次未提交');
        submitted = true;
        const response = await (resource ? updateComponentResource : updateTemplate)(previous.id, payload, { keepalive: true });
        if (this._isDestroyed || !this.contain.isCurrentEditorSave(ticket)) return;
        if (!store.getters['auth/hasResourcePermission'](permission + ':query') || !store.getters['auth/hasResourcePermission'](permission + ':save')) throw new Error('模板权限已变更，请重新加载确认保存结果');
        this.contain.templateRecord = (resource ? savedComponentResource : savedTemplate)(response, previous, payload.coverFileId);
        this.contain.completeEditorSave(ticket);
        if (!options.quiet) this.$message.success(this.contain.editorDirty ? '模板本次修改已保存，仍有新的修改未保存' : '模板已保存为新版本');
        return { record, signature: ticket.signature };
      } catch (error) {
        if (this.contain.isComponentTemplateEditing && submitted && !this._isDestroyed && this.contain.isCurrentEditorSave(ticket) && (!error.status || error.status >= 500 || [401, 403, 404, 409].includes(error.status))) {
          this.contain.templateSaveBlocked = error.status === 409 ? '模板已被其他窗口修改，当前编辑仍保留；请确认后重新加载，不能覆盖旧版本。' : '模板保存结果或权限未确认，当前编辑仍保留；请重新加载核对，勿重复提交。';
        }
        if (!this._isDestroyed && this.contain.failEditorSave(error, ticket)) this.$message.error(error.message || '模板保存失败');
      }
    },
    async reloadTemplate () {
      if (!this.contain.isComponentTemplateEditing || this.saving) return false;
      const id = this.contain.id, generation = this.contain.editorSaveGeneration;
      try {
        await this.$confirm('重新加载会放弃当前未保存的模板编辑，读取资源库中的最新内容。大屏中已使用的副本不受影响，是否继续？', '重新加载组件模板', { confirmButtonText: '放弃编辑并重新加载', cancelButtonText: '保留当前编辑', type: 'warning', closeOnClickModal: false });
        if (this._isDestroyed || id !== this.contain.id || generation !== this.contain.editorSaveGeneration || this.saving) return false;
        await this.contain.$refs.container.initData();
        return this.contain.editorSaveReady;
      } catch (error) { if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '模板重新加载失败'); return false; }
    },
    handleAdd (option, first = false) {
      if (!this.contain.projectRecord) return;
      this.contain.rememberStructure();
      let obj = this.deepClone(option);
      limitNodeBounds([obj], this.contain.config);
      if(!option.left){
        obj.left = 0;
      }
      if(!option.top){
        obj.top = 0
      }
      obj.index = uuid();
      obj.locked = false;
      if (first) {
        this.contain.nav.unshift(obj);
      } else {
        this.contain.nav.push(obj);
      }
      this.contain.active = [obj.index];
      return obj;
    },
    handleHome(){
      // Vue Router 3.1 在 next(false) 时拒绝 Promise；用户取消离开不是报错。
      return this.$router.push(this.contain.isTemplateEditing ? { path: '/home', query: { section: this.contain.isComponentResourceEditing ? 'components' : 'templates', kind: this.contain.templateRecord ? this.contain.templateRecord.kind : 'project' } } : this.contain.projectRecord && this.contain.projectRecord.type === 'scada2d' ? { path: '/home', query: { section: 'scada2d' } } : { path: '/' }).catch(error => {
        if (error && error.name !== 'NavigationDuplicated' && !error._isRouter) this.$message.error(error.message || '返回大屏列表失败，请重试');
      });
    }
  }
}
</script>

<style>
.nav {
  border-bottom: 0 !important;
  height: 45px;
  line-height: 45px;
  overflow: hidden;
}
.nav__icon {
  margin-right: 5px;
}
.nav .el-submenu .el-submenu__title,
.nav .el-menu-item {
  height: 45px;
  line-height: 45px;
  font-size: 12px;
}
</style>
